import { useEffect, useState } from 'react';
import {
    FeatureState,
    PageNames,
    ProductTypes,
    RoutePaths
} from '../../../Constants/Common';
import { NavLink } from 'react-router-dom';
import { RepairRequestApi } from '../../../api/RepairRequestApi';
import LoadingContainer from '../../common/loadingContainer/LoadingContainer';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import PageTitle from '../../common/PageTitle';
import ReloadHandler from '../../common/ReloadHandler';
import { useSelectedVehicle } from '../../hooks/useSelectedVehicle';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import ProgressButton from 'components/common/ProgressButton';
import Selectable from 'components/common/Selectable';
import { RepairRequest } from '../../../api/RepairRequestTypes';
import { TranslationHelper } from '../../../classes/TranslationHelper';
import { useLocaleFormatter } from '../../hooks/useLocaleFormatter';
import { useAppSelector } from 'state/hooks';
import { selectIsUSSite } from 'state/selectors/site';

const ExistingRepairPage = (): JSX.Element => {
    const { getAccessTokenSilently } = useAuth0();
    const { t } = useTranslation();
    const { toLocalDate } = useLocaleFormatter();
    const features = useAppSelector((s) => s.site.features);
    const showBankDetails = useAppSelector((s) => s.site.showBankDetails);
    const product = useAppSelector((s) => s.vehicles.selectedPolicy);
    const isUSSite = useAppSelector(selectIsUSSite);
    useSelectedVehicle(); //this hook redirects back to the landing page if the vehicle is not in the context (i.e. page refresh)

    const [loading, setLoading] = useState(true);
    const [repairRequests, setRepairRequests] = useState<RepairRequest[]>([]);
    const [selectedRepairRequest, setSelectedRepairRequest] =
        useState<RepairRequest>(null);
    const [enableClaimDocumentsButton, setEnableClaimDocumentsButton] =
        useState(false);
    const pageTitle =
        product.type === ProductTypes.GAP
            ? t('claim_requests')
            : t('repair_requests');

    const referenceNumber =
        product.type === ProductTypes.GAP
            ? t('claim_reference')
            : t('repair_reference');

    const requestDate =
        product.type === ProductTypes.GAP
            ? t('claimrequest_date')
            : t('repair_request_date');

    useEffect(() => {
        loadRepairRequests();
        GoogleAnalytics.setPageView(PageNames.PriorRepairRequest);
    }, []);

    const loadRepairRequests = async () => {
        setLoading(true);

        const successCallback = (result: RepairRequest[]) => {
            setRepairRequests(result);
            setLoading(false);
        };

        const failureCallback = () => {
            setLoading(false);
        };

        const token = await getAccessTokenSilently();

        new RepairRequestApi().getRepairRequests(
            product.reference,
            token,
            successCallback,
            failureCallback
        );
    };

    const renderErrorMessage = () => {
        if (!repairRequests || repairRequests.length < 1) {
            return <p className='error'>{t('no_prior_repair_requests')}</p>;
        }
    };

    const onRepairRequestSelection = (
        selected: boolean,
        repairRequest: RepairRequest,
        enableClaimDocumentsButton: boolean
    ) => {
        if (selected) {
            setSelectedRepairRequest(repairRequest);
        } else {
            setSelectedRepairRequest(null);
        }

        isUSSite && setEnableClaimDocumentsButton(enableClaimDocumentsButton);
    };

    return (
        <LoadingContainer loading={loading} message={t('loading')}>
            <div className='card page-content'>
                <PageTitle title={pageTitle} />
                <div className='card-body'>
                    {repairRequests.map((repairRequest, index) => (
                        <Selectable
                            id={`selectable-product-${index.toString()}`}
                            onClicked={(selected) =>
                                onRepairRequestSelection(
                                    selected,
                                    repairRequest,
                                    repairRequest.type === ProductTypes.GAP
                                )
                            }
                            selected={
                                repairRequest.referenceNumber ===
                                selectedRepairRequest?.referenceNumber
                            }
                            hasSelectableButton={true}
                            style={{
                                border: '1px solid #ced4da',
                                marginTop: '10px'
                            }}
                            key={`product-selectable-${index.toString()}-${index.toString()}`}>
                            <div
                                id={`repair-request-${index.toString()}`}
                                key={index}>
                                <b>{referenceNumber}:</b>{' '}
                                {repairRequest.referenceNumber}
                                <br />
                                <b>{requestDate}:</b>{' '}
                                {toLocalDate(repairRequest.created)}
                                <br />
                                <b>{t('status')}:</b>{' '}
                                {TranslationHelper.getTranslatedText(
                                    repairRequest.status
                                )}
                                <br />
                                <b>{t('last_updated')}:</b>{' '}
                                {toLocalDate(repairRequest.lastUpdated)}
                            </div>
                        </Selectable>
                    ))}
                </div>
            </div>
            <div className='bottom-navigation'>
                {renderErrorMessage()}
                <ProgressButton
                    navRoute={RoutePaths.ClaimDocuments}
                    locationState={{
                        referenceNumber: selectedRepairRequest?.referenceNumber, // TODO: remove and use repairRequest
                        repairRequest: selectedRepairRequest
                    }}
                    buttonStyle={`btn btn-primary progress-button ${!enableClaimDocumentsButton && 'disabled'}`}
                    text={t('claim_documents')}
                />
                {showBankDetails &&
                    features.BankAccount === FeatureState.On &&
                    repairRequests &&
                    repairRequests.length > 0 && (
                        <ProgressButton
                            navRoute={RoutePaths.RepaymentSetup}
                            locationState={{
                                referenceNumber: product.reference,
                                repairRequest: selectedRepairRequest
                            }}
                            buttonStyle={`btn btn-primary progress-button enabled`}
                            text={t('manage_bank_details')}
                        />
                    )}
                <NavLink
                    to={RoutePaths.Home}
                    className='btn btn-secondary progress-button'>
                    {t('back')}
                </NavLink>
            </div>
            <ReloadHandler reloadPath={RoutePaths.LandingPage} />
        </LoadingContainer>
    );
};

export default ExistingRepairPage;
